import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useMemo, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Avatar from 'components/field/Avatar';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import NoRecordsFound from 'components/global/NoRecordsFound';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import ProductForm from 'pages/Products/Products/ProductForm';

import { AUTOCOMPLETE } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';

import { faPlus } from '@fortawesome/pro-light-svg-icons';

import * as S from '../OTOLink.styled';

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const AddProduct = ({ mode }) => {
	const { Currency } = useLocale();
	const { settings: otoLinkSettings } = useSelector((state) => state.otoLinkSettings);
	const addMode = mode !== 'edit' && mode !== 'view';
	const viewMode = mode === 'view';
	const editMode = mode === 'edit';
	const {
		control,
		watch,
		setValue,
		formState: { isSubmitting, isLoading: isFormLoading, errors },
		getValues,
		setFocus
	} = useFormContext();
	const isAuthorized = useAccessControl();

	const isLoading = isSubmitting || isFormLoading;
	const productDetailsAccordionRef = useRef(null);
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const productAutocompleteLabels = useMemo(() => ['name'], []);
	const productAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);
	const darkMode = useSelector((state) => state.layout.darkMode);
	const defaultImage = darkMode
		? 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptyProduct-darkMode.png'
		: 'https://storage.googleapis.com/tryoto-public/public/assets/images/emptyProduct.jpg';

	const columns = useMemo(
		() => [
			{
				id: 'products',
				header: 'productName',
				size: 200,
				required: true,
				renderCell: ({ row }) => (
					<S.Flex>
						{watch(`items[${row.index}].productID`) || viewMode ? (
							<div>
								<ProductImage
									data={{
										image: watch(`items[${row.index}].image`)
									}}
								/>
							</div>
						) : (
							<Avatar
								shape="square"
								defaultImage={defaultImage}
								value={watch(`items[${row.index}].image`)}
								setValue={(value) => {
									setValue(`items[${row.index}].image`, value, validateOptions);
								}}
								style={{
									width: '4.5rem',
									height: '4.5rem',
									margin: '0'
								}}
							/>
						)}
						<S.FullWidth>
							<AutoComplete
								key={row.original?.id}
								autoCompleteName={AUTOCOMPLETE.PRODUCTS_BY_CURRENCY}
								value={watch(`items[${row.index}].productID`)}
								columns={productAutocompleteColumns}
								fixedContainerWidth={false}
								name={`items[${row.index}].name`}
								selectionLabelAccessor={productAutocompleteLabels}
								filterObject={{
									currencies:
										otoLinkSettings?.paymentMethod === 'COD'
											? [Currency]
											: otoLinkSettings?.allowedCurrencies
								}}
								setValue={(value, isFirstRender) => {
									if (!isFirstRender) {
										setValue(`items[${row.index}].productID`, value?.ID ?? null, validateOptions);
										setValue(`items[${row.index}].name`, value?.name ?? null, validateOptions);
										setValue(`items[${row.index}].price`, value?.price ?? null, validateOptions);
										setValue(
											`items[${row.index}].currency`,
											value?.currency ?? null,
											validateOptions
										);

										setValue(
											`items[${row.index}].total`,
											(value?.price ? value?.price + (value?.tax ?? 0) : 0) *
												getValues(`items[${row.index}].quantity`),
											validateOptions
										);
										setValue(`items[${row.index}].image`, value?.image ?? null, validateOptions);
										setValue(`items[${row.index}].tax`, value?.tax ?? null, validateOptions);
									}
								}}
								firstItem={
									isAuthorized('/autocomplete/product/addANewProduct')
										? watch(`items[${row.index}].name`)
											? {
													action: () => setFocus(`items[${row.index}].description`),
													label: (
														<Typography color="var(--blue)">
															continueAsANewProduct
														</Typography>
													)
												}
											: {
													action: () =>
														NiceModal.show(ProductForm, {
															setProduct: (value) => {
																setValue(
																	`items[${row.index}].productID`,
																	value,
																	validateOptions
																);
															},
															isImportOrder: false
														}),
													label: <Typography color="var(--blue)">addNewProduct</Typography>
												}
										: undefined
								}
								textValue={watch(`items[${row.index}].name`)}
								setTextValue={(value) => {
									setValue(`items[${row.index}].productID`, null, validateOptions);

									setValue(`items[${row.index}].name`, value, validateOptions);
								}}
								disabled={viewMode || editMode}
							/>
						</S.FullWidth>
					</S.Flex>
				),
				showOnMobile: true,
				enableHiding: false
			},
			{
				header: 'description',
				accessorKey: 'description',
				size: 100,
				maxSize: 100,
				renderCell: ({ row }) => (
					<TextArea
						className="description-textarea"
						disabled={viewMode || editMode}
						name={`items[${row.index}].description`}
						rows="2"
						placeholder="orderCommentsPlaceholder"
					/>
				)
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 90,
				maxSize: 90,
				required: true,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<IntegerInput
						errorMessage={errors?.items?.[row.index]?.quantity?.message}
						value={watch(`items[${row.index}].quantity`)}
						setValue={(value) => {
							setValue(`items[${row.index}].quantity`, value, validateOptions);
							const val =
								value *
								((getValues(`items[${row.index}].price`) ?? 0) +
									(getValues(`items[${row.index}].tax`) ?? 0));

							setValue(`items[${row.index}].total`, val ? Number(val.toFixed(2)) : val, validateOptions);
						}}
						disabled={viewMode || editMode}
					/>
				)
			},
			{
				header: 'price',
				accessorKey: 'price',
				size: 100,
				maxSize: 150,
				required: true,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<IntegerInput
						errorMessage={errors?.items?.[row.index]?.price?.message}
						value={watch(`items[${row.index}].price`)}
						setValue={(value) => {
							setValue(`items[${row.index}].price`, value, validateOptions);
							setValue(
								`items[${row.index}].total`,
								getValues(`items[${row.index}].quantity`) *
									((value ?? 0) + (getValues(`items[${row.index}].tax`) ?? 0)),
								validateOptions
							);
						}}
						unit={watch(`items[${row.index}].currency`)}
						disabled={viewMode || editMode}
						showArrows={false}
						allowFloat
					/>
				)
			},
			{
				header: 'total',
				accessorKey: 'total',
				size: 100,
				maxSize: 150,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<IntegerInput
						name={`items[${row.index}].total`}
						unit={watch(`items[${row.index}].currency`)}
						showArrows={false}
						allowFloat
						disabled={viewMode || editMode}
					/>
				)
			},
			...(!viewMode
				? [
						{
							id: 'deleteRow',
							size: isMobile ? 10 : 20,
							maxSize: 30,
							showOnMobile: true,
							enableHiding: false
						}
					]
				: [])
		],
		[isMobile, errors]
	);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'items'
	});

	const handleAddProduct = useCallback(() => {
		append({
			productID: null,
			image: null,
			name: null,
			price: null,
			quantity: 1,
			total: null,
			tax: null
		});
	}, []);

	return (
		<S.FullWidthAccordion
			ref={productDetailsAccordionRef}
			header="productDetails"
			headerTooltip="productDetailsAccordionTooltip"
			defaultVisible>
			<FormItem>
				<Table
					columns={columns}
					data={fields}
					loading={isLoading}
					noRecordsFound={
						viewMode ? (
							<NoRecordsFound
								lottieName="no-items"
								height="10rem"
								title="noItems"
								subTitle="noItemsSubtitle"
							/>
						) : (
							<></>
						)
					}
					onRowDelete={(row) => remove(row.index)}
					storageKey="orderDetailsTable"
					showColumnsMenu={!addMode}
				/>
			</FormItem>
			{!viewMode && !editMode && (
				<FormItem>
					<Button label="addNewRow" icon={faPlus} variant="outline" onClick={handleAddProduct} />
				</FormItem>
			)}
		</S.FullWidthAccordion>
	);
};

export default AddProduct;
